import moment from "moment";
import _ from "lodash";
import Page from "../common/navigation/Page";
import Button from "../common/F7/Button";
import ButtonSmall from "../common/F7/ButtonSmall";
import BackButton from "../common/navigation/BackButton";
import ListBlock from "../common/F7/ListBlock";
import ListBlockItem from "../common/F7/ListBlockItem";
import ContentTitle from "../common/F7/ContentTitle";
import Image from "../common/F7/Image";
import ListItemStyle from "../inventory/components/ListItemStyle";
import mapUtils from "../../utils/map";
import F7 from "../../utils/F7";
import config from "../../config";
import React, { FC, Fragment, useCallback, useState } from "react";
import usePermissions from "../../hooks/usePermissions";
import { useMap } from "../../App";
import { useNavigate } from "react-router-dom";
import { useJWT } from "../../state";
import Spinner from "../common/F7/Spinner";
import { InventoryFieldValue, InventoryFormdataItem } from "../../hooks/database/types/inventories";
import { useDeleteObject } from "../../hooks/database/useDeleteObject";
import { useCreateObjectForm, useUpdateObject } from "../../hooks/database/useUpdateObject";
import { useCurrentInventory } from "../../hooks/database/useCurrentInventory";
import { InventoryObject, useCurrentObject } from "../../hooks/database/useCurrentObject";
import DangerZoneCard from "../common/F7/DangerZoneCard";
import { PouchDocument } from "../../hooks/database/common";
import { useSessionStorage } from "../../hooks/useStorage";
import "./ObjectPage.css";

export const stringifyObject = (object: Record<string, any>) => {
  let string = "{";
  Object.entries(object).forEach(([key, value]) => (string += `"${key}":"${value}";`));
  const index = string.lastIndexOf(";");
  string = string.substring(0, index) + string.substring(index + 1, string.length);
  string += "}";
  return string;
};

const FormStyleItem: FC<{
  formStyling:
    | null
    | {
        data: PouchDocument<InventoryFormdataItem>;
        rgb: { fill: string; stroke: string; id: string; label: string; styleable: boolean };
      }[];
  doc: InventoryFormdataItem & Partial<PouchDB.Core.IdMeta>;
  object: InventoryObject;
  changeEditToggle: boolean;
  handleFormSelectedStyle: (formDoc: InventoryFormdataItem & Partial<PouchDB.Core.IdMeta>, currForm: any) => Promise<void>;
}> = ({ formStyling, doc, object, changeEditToggle, handleFormSelectedStyle }) => {
  const [formList, _] = useSessionStorage("formList");

  const currForm = formStyling?.find((form) => doc._id === form.data._id);
  if (!currForm) {
    return null;
  }
  const featureType = object.geom.type;
  const currSelected = object.data && currForm.data._id && object.data[object.data.length - 1]._id === currForm.data._id;
  if (formList === "false") {
    return null;
  } else if (currForm && currSelected && changeEditToggle && currForm.rgb.styleable) {
    return (
      <i onClick={() => handleFormSelectedStyle(doc, currForm)}>
        <ListItemStyle featureType={featureType} color={currForm.rgb}>
          <rect
            width="30"
            height="30"
            style={{
              fill: "white",
              stroke: "black",
              strokeWidth: "2px",
            }}
          />
        </ListItemStyle>
      </i>
    );
  } else {
    return (
      <i onClick={() => handleFormSelectedStyle(doc, currForm)}>
        <ListItemStyle featureType={featureType} color={currForm.rgb} />
      </i>
    );
  }
};

async function prompt(text: string, title: string, prepare: (modal: HTMLElement) => void) {
  return new Promise<string>((resolve, reject) => {
    const modal = F7.prompt(text, title, resolve, () => reject());
    prepare(modal);
  });
}

function incrementStringCounter(value: string) {
  const digits = /(\d+)$/.exec(value);
  if (digits && digits.length > 0) {
    const number = parseInt(digits[0]) + 1;
    return `${value.slice(0, value.length - digits[0].length)}${number}`;
  }
  return value;
}

const ObjectPage: FC = function () {
  const navigate = useNavigate();

  const { data: object } = useCurrentObject();
  const { execute: updateObject } = useUpdateObject();
  const { execute: createForm } = useCreateObjectForm();

  const { data: inventory } = useCurrentInventory();

  const [changeEditToggle, setChangeEditToggle] = useState(false);
  const [changeSortToggle, setChangeSortToggle] = useState(
    !!(sessionStorage.getItem("defaultCreateDate") && sessionStorage.getItem("defaultCreateDate") === "true") || false
  );

  const { canCreateFormFromTemplate, canToggleInInvForms, canChangeTitleInInvForms, canChangePositionInInvForms, canDeleteGeom } =
    usePermissions();

  const handleSortToggle = useCallback(() => {
    setChangeSortToggle((v) => !v);
  }, []);
  const handleEditToggle = useCallback(() => {
    setChangeEditToggle((v) => !v);
  }, []);

  const map = useMap()!;
  const decodedJwt = useJWT()!;

  const formStyling = object ? mapUtils.getFormStyleMapping(mapUtils.inventoryStyle, object) : null;

  const handleSaveFormCopy = useCallback(
    async (formDocument: PouchDocument<InventoryFormdataItem>) => {
      if (!inventory || !object) {
        return;
      }

      const currFormKey = Object.keys(inventory.forms[formDocument.form].fields)[0];
      const value = formDocument.values[currFormKey];

      const title = await prompt("Form Name:", "Copy Form", (modal) => {
        const input = modal.querySelector<HTMLInputElement>(".modal-text-input")!;
        if (value.type === "number" || value.type === "text") {
          input.setAttribute("type", value.type);
        } else {
          input.setAttribute("disabled", "true");
        }
        input.value = incrementStringCounter((value.value as string) || "");
      });
      if (!title) {
        F7.alert("", "Form Name is required");
        return;
      }

      const docId = object._id + "_" + moment().format("YYYY-MM-DDTHH:mm:ss");
      const updated = moment().format();

      const docClone: PouchDocument<InventoryFormdataItem> = {
        type: "formdata",
        _id: docId,
        legacyId: moment().format("YYYY-MM-DDTHH:mm:ss"),
        geomId: object._id,
        updated,
        created: formDocument.created,
        version: formDocument.version,
        form: formDocument.form,
        docInfo: btoa(
          stringifyObject({
            origin: "PIANO",
            created: updated,
            cuser: decodedJwt.userId,
            cprod: config.productionBuild,
            uorigin: "",
            updated: "",
            uuser: "",
            uprod: "",
          })
        ),
        addInfo: btoa(
          stringifyObject({
            isApp: config.isApp,
            appVersion: config.version,
            userAgent: window.navigator.userAgent,
            onLine: window.navigator.onLine,
          })
        ),
        values: _.cloneDeep(formDocument.values),
      };
      docClone.values[currFormKey].value = title;
      const showModal = Object.values(docClone.values).some(
        (field) => field.type === "image" && field.value && Array.isArray(field.value) && field.value.length > 0
      );
      if (showModal) {
        F7.modal({
          title: "Copy Form",
          text: "Keep Images In Copy?",
          buttons: [
            {
              text: "No",
              onClick: async () => {
                for (let key in docClone.values) {
                  if (docClone.values[key].type === "image") {
                    docClone.values[key].value = null;
                  }
                }
                await createForm(docClone);
              },
            },
            {
              text: "Yes",
              onClick: () => createForm(docClone),
            },
          ],
        });
      } else {
        await createForm(docClone);
      }
    },
    [inventory, object, decodedJwt.userId, createForm]
  );

  const handleButtonCloseClick = useCallback(() => {
    navigate("../../");
  }, [navigate]);

  const handleChangeTitleButtonClick = useCallback(() => {
    if (!object) {
      return;
    }
    const prompt = F7.prompt("Enter the new title", "", async (title: string) => {
      // disallow changing to an empty title
      if (title.trim() === "") {
        return;
      }
      await updateObject({
        ...object,
        title,
      });
    });
    // pre-populate input with current title, focus and select it
    const input = prompt.querySelector<HTMLInputElement>("input.modal-text-input");
    input!.value = object.title;
    input!.focus();
    input!.setSelectionRange(0, object.title.length);
  }, [object, updateObject]);

  const { execute: deleteObject } = useDeleteObject();
  const handleDeleteClick = useCallback(() => {
    F7.confirm("This action cannot be undone", "Delete Geom?", async () => {
      if (!object) {
        return;
      }
      await deleteObject(object);
      navigate("../../");
    });
  }, [object, deleteObject, navigate]);

  const handleFormSelectedStyle = useCallback(
    async (formDoc: InventoryFormdataItem & Partial<PouchDB.Core.IdMeta>, currForm: any) => {
      if (!object) {
        return;
      }
      if (changeEditToggle && currForm.rgb.styleable === true) {
        const extent = map.getView().calculateExtent(map.getSize());
        sessionStorage.setItem("reExtent", JSON.stringify(extent));
        const selectedData = object.data.find((el) => el._id === formDoc._id)!;
        const newDataList = object.data.filter((el) => el._id !== formDoc._id);
        await updateObject({
          ...object,
          data: [...newDataList, selectedData],
        });
      }
    },
    [changeEditToggle, map, object, updateObject]
  );

  if (!object || !inventory) {
    return <Spinner />;
  }

  const forms = inventory.forms;
  if (
    sessionStorage.getItem("formListCurrentStyling") &&
    sessionStorage.getItem("formListCurrentStyling") === "false" &&
    changeEditToggle
  ) {
    sessionStorage.setItem("formListTempStyling", "true");
  } else {
    sessionStorage.setItem("formListTempStyling", "false");
  }
  const formDataGroupedByForm = _.groupBy(
    object.data.sort((a, b) => {
      let sortBy;
      if (sessionStorage.getItem("forceCreatedDate") && sessionStorage.getItem("forceCreatedDate") === "true") {
        // @ts-ignore
        sortBy = moment(b.legacyId) - moment(a.legacyId);
      } else if (sessionStorage.getItem("forceUpdatedDate") && sessionStorage.getItem("forceUpdatedDate") === "true") {
        // @ts-ignore
        sortBy = moment(b.updated) - moment(a.updated);
      } else if (changeSortToggle) {
        // @ts-ignore
        sortBy = moment(b.legacyId) - moment(a.legacyId);
      } else {
        // @ts-ignore
        sortBy = moment(b.updated) - moment(a.updated);
      }
      return sortBy;
    }),
    (r) => r.form
  );

  return (
    <Page navbarLeft={<BackButton onClick={handleButtonCloseClick}>Close</BackButton>} title={object.title} noToolbar>
      {canCreateFormFromTemplate ? (
        <div>
          <ContentTitle>Create form from template</ContentTitle>
          <ListBlock>
            {Object.values(forms).map((form) => (
              <ListBlockItem key={form.key} title={form.name} to={`f/new/${form.key}`} />
            ))}
          </ListBlock>
        </div>
      ) : null}
      <ContentTitle>Existing Forms</ContentTitle>
      <div
        style={{
          float: "right",
          marginRight: 110,
          marginTop: -35,
        }}
      >
        {canToggleInInvForms && object.data.length > 0 ? (
          <ButtonSmall onClick={handleEditToggle}>{changeEditToggle ? "Done copying" : "Copy form"}</ButtonSmall>
        ) : null}
      </div>
      {sessionStorage.getItem("hideSortToggle") && sessionStorage.getItem("hideSortToggle") === "true" ? null : (
        <div
          style={{
            float: "right",
            marginRight: 10,
            marginTop: -35,
          }}
        >
          {object.data.length > 0 ? <ButtonSmall onClick={handleSortToggle}>Toggle date</ButtonSmall> : null}
        </div>
      )}
      <ListBlock>
        {Object.entries(formDataGroupedByForm).map(([form, value]) => (
          <Fragment key={form}>
            <li className="list-group-title" style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{forms[form].name}</span>
              <span>Showing {changeSortToggle ? "created" : "updated"} date</span>
            </li>
            {value.map((doc) => {
              // WARNING: doc.values might be empty in which case field will be undefined
              const field = doc.values[Object.keys(doc.values)[0]] as InventoryFieldValue | undefined;
              return (
                <ListBlockItem
                  key={doc._id}
                  title={
                    <span style={doc._deleted ? { opacity: 0.5, textDecoration: "line-through" } : {}}>
                      {!field ? (
                        <i>Form does not have any fields</i>
                      ) : field.type === "image" ? (
                        field.value?.[0] ? (
                          <Image
                            src={field.value?.[0] as any}
                            style={{
                              height: 35,
                              borderRadius: 3,
                            }}
                          />
                        ) : (
                          "no value"
                        )
                      ) : (
                        field.value || "no value"
                      )}
                    </span>
                  }
                  after={
                    changeSortToggle
                      ? doc._deleted
                        ? "DELETED"
                        : "C " + moment(doc.legacyId).format("YYYY-MM-DD HH:mm")
                      : doc._deleted
                      ? "DELETED"
                      : "U " + moment(doc.updated).format("YYYY-MM-DD HH:mm")
                  }
                  media={
                    changeEditToggle ? (
                      <i
                        className="fa fa-copy"
                        style={{
                          color: "green",
                          paddingLeft: 10,
                          fontSize: 24,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveFormCopy(doc);
                        }}
                      />
                    ) : null
                  }
                  styleIcon={
                    <FormStyleItem
                      formStyling={formStyling}
                      doc={doc}
                      object={object}
                      changeEditToggle={changeEditToggle}
                      handleFormSelectedStyle={handleFormSelectedStyle}
                    />
                  }
                  to={`f/${doc._id}`}
                />
              );
            })}
          </Fragment>
        ))}
      </ListBlock>

      <hr />

      {canChangeTitleInInvForms ? <Button onClick={handleChangeTitleButtonClick}>Rename</Button> : null}
      {canChangePositionInInvForms ? <Button to="modify">Modify Geometry</Button> : null}
      {object.data.length > 0 ? <Button to="preview">Preview all forms</Button> : null}

      {canDeleteGeom ? (
        <DangerZoneCard header="Danger zone">
          <Button color="red" onClick={handleDeleteClick}>
            Delete Geom
          </Button>
        </DangerZoneCard>
      ) : null}
    </Page>
  );
};

export default ObjectPage;
